/**
 * SSR-safe lazy loading utility for React components.
 *
 * Supports both **named exports** (pass `exportName` as the 2nd arg) and **default exports**
 * (omit `exportName`). Works correctly with @loadable/babel-plugin for server-side rendering
 * by exporting the function as `loadable`, which the babel plugin recognizes through
 * name-based pattern matching on the local identifier.
 *
 * Adds silent retry on chunk-load failure (2 attempts, 1s/4s backoff) to recover from
 * transient mobile-network errors. See doc/plans/PRNT-4319-chunk-load-retry.md for the
 * original analysis and doc/plans/chunk-retry-backoff.md for the current retry behavior.
 *
 * @module @shared/core/loadableFromNamedExport
 */

import type { DefaultComponent, LoadableComponent, LoadableLibrary } from '@loadable/component';
import loadableOriginal from '@loadable/component';
import type { ComponentProps, ComponentType } from 'react';
import type React from 'react';

import globalWindow from '@shared/core/globals';
import i18n from '@shared/core/i18n/i18n';
import { addAction } from '@shared/utils/logger';

// Extracts the original props type from React components without the implicit children prop.
// React.FC<Props> automatically adds `children?: ReactNode` to the props type, but we want
// to preserve the exact props interface as defined by the component author.
// This ensures LoadableComponent<Props> matches the original component's props signature.
// - First checks React.FC<P> and extracts P (the original props without children)
// - Then checks React.ComponentType<P> and extracts P (covers other component patterns)
// - Falls back to ComponentProps<T> for intrinsic elements (div, span, etc.)
// - Returns never for non-component types (should not occur in practice)
type ExtractComponentProps<T> =
  T extends React.FC<infer P>
    ? P
    : T extends React.ComponentType<infer P>
      ? P
      : T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<any> // eslint-disable-line @typescript-eslint/no-explicit-any
        ? ComponentProps<T>
        : never;

// Number of silent retry attempts after the original load fails. Attempt 1 alone recovers
// ~28% of failures in prod; NR shows the remaining exhaustion pool skews to flaky-but-alive
// connections (timeouts on 3g/4g, page visible, navigator.onLine true), and 65% of sessions
// that hit any chunk failure hit 2+ — i.e. a single 1s retry usually fires while the network
// is still down. A second, later attempt targets exactly that population.
const MAX_RETRIES = 2;
// Delay before each attempt, indexed by `attempt - 1`. Exponential-ish rather than uniform:
// attempt 1 stays at 1s (fast recovery for a momentary blip, unchanged from PRNT-4319),
// attempt 2 waits 4s to give a recovering radio time to come back.
const RETRY_DELAYS_MS = [1000, 4000];
// Ceiling on a single retry ATTEMPT as a whole — the injected `<script>` and the `loader()`
// call share it rather than each getting their own, since they run back to back and a slow but
// successful script does not throw. Capping them independently would let one attempt reach ~30s
// and the sequence ~65s. Webpack 4's default `chunkLoadTimeout` is 120s, far too long once the
// original load has already failed.
//
// A flat per-attempt cap rather than a total-sequence budget, deliberately. The worst case is
// bounded by construction: 1s + 15s + 4s + 15s = 35s of loading fallback before the error
// boundary takes over. A running budget bought a slightly tighter 40s but needed arithmetic at
// four checkpoints, and every one of those checkpoints was a bug at some point (unbounded
// loader, backoff overshoot, an unset flag, and a negative timeout when a suspended tab
// stretched the sleep). Simple first; revisit with data if the cap turns out to be wrong.
//
// 15s is ~7x the measured need: the worst-offender chunks transfer in ~2.1s on Slow 3G with
// real production brotli bytes. The previous 30s predates that measurement.
const RETRY_ATTEMPT_TIMEOUT_MS = 15000;

const isChunkLoadError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const name = (err as { name?: string }).name;
  const message = (err as { message?: string }).message ?? '';
  // The character class includes `/` and `.` because joy-web's named webpack chunks
  // routinely use path-style identifiers (e.g. "content/contactCollector/iframe",
  // "content/admin/photo-upload"), and `~` because that is webpack's default splitChunks
  // `automaticNameDelimiter` ("vendors~app") — no such chunk exists in the current build, since
  // every cacheGroup is named explicitly, but this fallback should not stop retrying if that
  // changes. The `name === 'ChunkLoadError'` check covers the
  // normal path; this message fallback exists for re-thrown errors where the name
  // is lost (e.g. wrapped by a transform), and we don't want to silently drop those.
  return name === 'ChunkLoadError' || /Loading chunk [\w/.~-]+ failed/.test(message);
};

// Webpack 4 attaches the failed chunk URL as `err.request` (see the JSONP runtime in
// node_modules/webpack/lib/web/JsonpMainTemplate.runtime.js — the Error object stamped on
// chunk load failure has `error.request = realSrc`). We use it to manually re-load the
// chunk with a cache-busting query param without needing access to webpack-internal
// chunk-URL builders (webpack 4 has no `__webpack_require__.u`).
const extractFailedChunkUrl = (err: unknown): string | undefined => {
  const request = (err as { request?: unknown } | null | undefined)?.request;
  return typeof request === 'string' ? request : undefined;
};

/**
 * Set `key` on a URL's query string, replacing any existing occurrence rather than appending a
 * second one — with more than one retry attempt the URL handed to attempt 2 can already carry
 * `?retry=1`, and `?retry=1&retry=2` still cache-busts but splits one logical chunk across two
 * URL shapes in CDN logs and NR facets.
 *
 * Uses `URL`/`searchParams` rather than string surgery so fragments, a stray trailing `?` and
 * separator placement are the platform's problem, not ours. Resolved against
 * `location.href` because `webChunkPublicPath` is path-absolute (`/bliss-apps/joy-web/web`), so
 * `err.request` can arrive without a host — the same base `normalizeChunkUrl` uses.
 *
 * Absolutizing a host-less input is harmless: the browser would resolve it against the same
 * origin anyway, and this only ever feeds a `<script src>`.
 *
 * Falls back to the original URL if parsing fails, so a malformed `err.request` still gets a
 * retry (just without the cache-bust) rather than throwing a `TypeError` out of the retry loop,
 * where it would be misclassified as a broken module and propagated instead of retried.
 */
const withQueryParam = (url: string, key: string, value: string | number): string => {
  try {
    const parsed = new URL(url, globalWindow.location?.href);
    parsed.searchParams.set(key, String(value));
    return parsed.href;
  } catch {
    return url;
  }
};
/**
 * Normalize a chunk URL into host + path + filename for NR faceting.
 * Avoids logging full URLs (which include content-hashed filenames that change per deploy).
 */
const normalizeChunkUrl = (url: string): { failedUrlHost: string; failedUrlPath: string } => {
  try {
    const parsed = new URL(url, globalWindow.location?.href);
    const pathParts = parsed.pathname.split('/');
    const filename = pathParts.pop() ?? '';
    // Strip the content hash: "registryList.b09dfb3e.js" → "registryList.js"
    const normalized = filename.replace(/\.[a-f0-9]{8}\.js$/, '.js');
    return {
      failedUrlHost: parsed.host,
      failedUrlPath: pathParts.join('/') + '/' + normalized,
    };
  } catch {
    return { failedUrlHost: 'unknown', failedUrlPath: url };
  }
};

/** Snapshot client environment at the moment of failure for NR classification. */
const captureClientContext = (): Record<string, string | number | boolean | undefined> => {
  const nav = globalWindow.navigator as (Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }) | undefined;
  return {
    online: nav?.onLine,
    visibilityState: globalWindow.document?.visibilityState,
    connectionEffectiveType: nav?.connection?.effectiveType,
    connectionSaveData: nav?.connection?.saveData,
  };
};

/**
 * Extract a low-cardinality error reason from a chunk-load error message.
 * Raw messages contain full URLs with content hashes (deploy churn), so we
 * classify into a small set of reason strings instead of logging the raw text.
 */
const classifyChunkErrorReason = (err: unknown): string => {
  const message = String((err as { message?: string }).message ?? '');
  if (/timeout/i.test(message)) return 'timeout';
  if (/missing/i.test(message)) return 'missing';
  if ((err as { name?: string }).name === 'ChunkLoadError') return 'chunk-load-error';
  if (/Loading chunk/.test(message)) return 'chunk-load-error';
  return 'unknown';
};

// Manually inject a `<script>` for the chunk URL with a cache-bust query param. When the
// script loads, webpack's JSONP callback (`window["__LOADABLE_LOADED_CHUNKS__"].push(...)`)
// runs from inside the chunk's bootstrap code, which marks `installedChunks[chunkId] = 0`
// and registers all module factories — so a subsequent call to the original `loader()`
// resolves immediately without making a second network request.
const loadScriptWithCacheBust = (failedUrl: string, attempt: number, timeoutMs: number): Promise<void> =>
  new Promise((resolve, reject) => {
    // `document` is the canonical reference here. SSR never reaches this path (async chunks
    // load synchronously via `requireSync` on the server), but guard defensively so an
    // accidental SSR call fails loudly instead of crashing with "document is not defined".
    // Checked BEFORE building the URL: without a document there is no `location` to resolve a
    // host-less `err.request` against.
    if (typeof document === 'undefined') {
      reject(new Error('Chunk retry attempted without a DOM'));
      return;
    }
    const url = withQueryParam(failedUrl, 'retry', attempt);
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.crossOrigin = 'anonymous';
    let timeoutId: ReturnType<typeof globalWindow.setTimeout> | undefined;
    const cleanup = () => {
      script.onload = null;
      script.onerror = null;
      if (timeoutId !== undefined) {
        globalWindow.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    script.onload = () => {
      cleanup();
      resolve();
    };
    script.onerror = () => {
      cleanup();
      script.remove();
      reject(new Error(`Chunk retry script failed: ${url}`));
    };
    // Without this, a stalled response (the 21% timeout-class failures we're meant to
    // recover) would leave the wrapper awaiting `onload`/`onerror` forever and the lazy
    // route stuck on its fallback. Mirrors webpack's own chunk-load-timeout behavior.
    timeoutId = globalWindow.setTimeout(() => {
      cleanup();
      script.remove();
      reject(new Error(`Chunk retry script timed out: ${url}`));
    }, timeoutMs);
    document.head.appendChild(script);
  });

/**
 * Classify how a retry attempt failed, for NR faceting on the Exhausted event. Distinguishes
 * "we never got the bytes" (script-error / script-timeout — a network problem, which more
 * backoff could plausibly fix) from "we got the bytes and the loader still rejected"
 * (loader-error — retrying is pointless, the chunk or its dependency graph is broken).
 */
const classifyRetryFailure = (err: unknown): 'script-error' | 'script-timeout' | 'loader-error' | 'loader-timeout' => {
  const message = String((err as { message?: string } | null | undefined)?.message ?? '');
  if (message.startsWith('Chunk retry script timed out')) return 'script-timeout';
  if (message.startsWith('Chunk retry script failed')) return 'script-error';
  if (message.startsWith('Chunk retry loader timed out')) return 'loader-timeout';
  return 'loader-error';
};

/**
 * Reject with `Chunk retry loader timed out` if `promise` has not settled within `ms`.
 *
 * The abandoned promise keeps running — a webpack chunk request cannot be cancelled — but
 * nothing consumes its result, exactly as an abandoned retry `<script>` is left to finish
 * after being removed from the DOM.
 */
const withDeadline = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = globalWindow.setTimeout(() => reject(new Error(`Chunk retry loader timed out after ${ms}ms`)), ms);
    promise.then(
      value => {
        globalWindow.clearTimeout(timeoutId);
        resolve(value);
      },
      error => {
        globalWindow.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });

const delay = (ms: number): Promise<void> => new Promise(resolve => globalWindow.setTimeout(resolve, ms));

// Wraps the chunk-load promise with retry. Orthogonal to `resolveComponent` (which runs
// AFTER the chunk loads successfully). The `chunkName` (when known) is sent with telemetry
// and lets us facet retry recovery rates by chunk in New Relic.
//
// Implemented as a flat loop rather than recursion on purpose. The recursive shape this
// replaced re-entered the whole wrapper per attempt, which made two things structurally
// impossible to get right once MAX_RETRIES exceeded 1:
//   1. a failed cache-bust `<script>` had no way to reach the next attempt — it had to emit
//      Exhausted and reject from inside the retry, so the only path to attempt 2 was
//      "script loaded but loader still rejected" (rare). The common case — dead radio, script
//      `onerror` — silently kept its single-attempt behavior.
//   2. every frame on the way out emitted its own ChunkRetrySuccess, so one recovery on
//      attempt 2 double-counted as attempt=2 AND attempt=1 in NR.
// One loop, one terminal event per load: exactly one of ChunkRetrySuccess or
// ChunkRetryExhausted is emitted per wrapped call that entered retry.
const withChunkRetry =
  <T>(loader: () => Promise<T>, chunkName?: string): (() => Promise<T>) =>
  async (): Promise<T> => {
    let originalErr: unknown;
    try {
      return await loader();
    } catch (err) {
      // Anything that isn't a chunk-load failure (a module that throws at evaluation time, a
      // rejected import of a genuinely missing export) is not a network problem — surface it
      // untouched and without telemetry, exactly as the pre-retry code did.
      if (!isChunkLoadError(err)) throw err;
      originalErr = err;
    }

    const failedUrl = extractFailedChunkUrl(originalErr);
    const urlParts = failedUrl ? normalizeChunkUrl(failedUrl) : {};
    // Facets describing the ORIGINAL failure. Held constant across attempts so every event
    // for one logical load groups under the same chunk/reason in NR.
    const facets = {
      chunkName,
      hasRequest: Boolean(failedUrl),
      ...urlParts,
      errorName: (originalErr as { name?: string }).name,
      errorReason: classifyChunkErrorReason(originalErr),
    };
    // The chunk to cache-bust next. Starts at the original failure but is RETARGETED whenever
    // a later attempt's `loader()` names a different chunk: one loadable import can require
    // several split chunks, so attempt 1 can fix chunk A and then fail on chunk B. Leaving
    // this pinned would re-inject A forever and request B without a cache-bust, letting a bad
    // cached response for B exhaust the retries. Deliberately separate from `facets`, which
    // stays pinned to the original failure so all events for one load group together in NR.
    let activeFailedUrl = failedUrl;
    const sequenceStartMs = Date.now();
    // How the final attempt failed, and how many we actually made. Counted rather than assumed
    // so an Exhausted event cannot claim 2 attempts when only 1 ran.
    let lastFailure: 'none' | 'script-error' | 'script-timeout' | 'loader-error' | 'loader-timeout' = 'none';
    let attemptsMade = 0;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // Captured BEFORE the sleep: the pre-backoff implementation timed from here too, so
      // `ChunkRetrySuccess.elapsedMs` keeps covering "backoff + fetch". Starting the clock
      // after the sleep would silently drop 1s (attempt 1) or 4s (attempt 2), showing existing
      // latency dashboards an artificial improvement on deploy.
      const attemptStartMs = Date.now();
      await delay(RETRY_DELAYS_MS[attempt - 1]);

      attemptsMade = attempt;
      addAction('ChunkRetryAttempt', {
        ...facets,
        attempt,
        cacheBust: Boolean(activeFailedUrl),
        ...captureClientContext(),
      });

      try {
        // If we have the failed URL, pre-load the chunk with a cache-bust query — this
        // recovers from CDN-edge-cache corruption (the Sydney pattern). When the script
        // loads, webpack's JSONP callback marks the chunk installed, so the loader() call
        // below resolves immediately from cache without a second network round-trip.
        // If the script itself fails we skip loader() for this attempt (it would only repeat
        // the request that just failed) and fall through to the next attempt, if any.
        // The cap covers the attempt as a WHOLE, so the script and the loader share it rather
        // than each getting a fresh 15s. They run back to back, and a script that succeeds
        // slowly does not throw — so independent caps let one attempt reach ~30s and the
        // sequence ~65s, the exact figure this cap exists to rule out.
        const operationStartMs = Date.now();
        if (activeFailedUrl) {
          await loadScriptWithCacheBust(activeFailedUrl, attempt, RETRY_ATTEMPT_TIMEOUT_MS);
        }
        // Bound `loader()` too, not just the injected script. When an import needs several
        // split chunks, the script can repair the first one and `loader()` can then sit pending
        // on another for webpack's 120s `chunkLoadTimeout`, parking the user on the loading
        // fallback long past the point the error boundary should have taken over.
        // `Math.max(0, …)` because a suspended tab can overrun the script phase: a negative
        // deadline would fire immediately and open-then-abandon the request in one tick.
        const remainingAttemptMs = Math.max(0, RETRY_ATTEMPT_TIMEOUT_MS - (Date.now() - operationStartMs));
        const result = await withDeadline(loader(), remainingAttemptMs);
        addAction('ChunkRetrySuccess', {
          ...facets,
          attempt,
          // Time spent on the winning attempt (matches the pre-backoff metric definition)
          // alongside total time the user waited across the whole sequence.
          elapsedMs: Date.now() - attemptStartMs,
          totalElapsedMs: Date.now() - sequenceStartMs,
          ...captureClientContext(),
        });
        return result;
      } catch (retryErr) {
        // A non-chunk rejection out of loader() means the bytes arrived and the module itself
        // is broken. More attempts can't fix that, and swallowing it into the original
        // ChunkLoadError would hide the real cause — propagate it as-is.
        if (!isChunkLoadError(retryErr) && classifyRetryFailure(retryErr) === 'loader-error') throw retryErr;
        lastFailure = classifyRetryFailure(retryErr);
        // Retarget at whatever chunk this attempt actually failed on. Script failures carry no
        // `request`, so those leave the current target in place rather than clearing it.
        const nextFailedUrl = extractFailedChunkUrl(retryErr);
        if (nextFailedUrl) activeFailedUrl = nextFailedUrl;
      }
    }

    const totalElapsedMs = Date.now() - sequenceStartMs;
    addAction('ChunkRetryExhausted', {
      ...facets,
      attempts: attemptsMade,
      elapsedMs: totalElapsedMs,
      lastFailure,
      ...captureClientContext(),
    });
    // Propagate the ORIGINAL chunk error, not the internal "Chunk retry script failed"
    // wrapper — error boundaries and NR error grouping expect a ChunkLoadError here.
    throw originalErr;
  };

// `@loadable/babel-plugin` rewrites `() => import('./X')` into a config object
// { requireAsync, requireSync, chunkName, resolved } in production builds. Handle both shapes.
type BabelLoadableConfig = {
  requireAsync: (...args: unknown[]) => Promise<unknown>;
  chunkName?: () => string;
  [key: string]: unknown;
};

const wrapLoaderArg = (arg: unknown): unknown => {
  if (typeof arg === 'function') {
    return withChunkRetry(arg as () => Promise<unknown>);
  }
  if (arg && typeof arg === 'object' && typeof (arg as BabelLoadableConfig).requireAsync === 'function') {
    const config = arg as BabelLoadableConfig;
    const chunkName = typeof config.chunkName === 'function' ? config.chunkName() : undefined;
    const originalRequireAsync = config.requireAsync;
    // The babel-plugin's `requireAsync` is a method that calls `this.resolve(...)` and
    // friends — wrap it as a `function` (not an arrow) so we can preserve `this` from the
    // call site (which is the config object loadable hands back to us) and forward args.
    const wrappedRequireAsync = function (this: unknown, ...args: unknown[]) {
      // The inner arrow inherits `this` lexically from this `function`, so `apply(this, …)`
      // forwards the caller's `this` (the config object loadable hands back) to the original.
      return withChunkRetry(() => originalRequireAsync.apply(this, args), chunkName)();
    };
    return { ...config, requireAsync: wrappedRequireAsync };
  }
  return arg;
};

/**
 * Loads a chunk's i18n namespaces together with the chunk, so the loadable resolves only once BOTH
 * are in place. Every `useTranslation()` inside the chunk then finds its bundle in the i18n store and
 * never suspends — which matters because the app runs legacy-mode Suspense (React 17
 * `ReactDOM.render`): a component that suspends inside an already-visible boundary hides that whole
 * boundary with `display:none`. In admin that boundary is usually the root one, so one late
 * namespace fetch blanks the entire page.
 *
 * WHICH namespaces: `window.__I18N_CHUNK_NAMESPACES__[chunkName]` — the build's attribution of chunk
 * group → namespaces from bundler stats (config/translationsManifestPlugin/chunkNamespaces.ts),
 * installed by the i18n store script on SSR pages and by an inline tag on static HTML. Exact, covers
 * every chunk, no per-lazy code. Read at LOAD time, not definition time: the map is set by a
 * parser-blocking script, but a loadable may be defined by a module that evaluates before an inline
 * tag placed later in the document.
 *
 * Applies to the `@loadable/babel-plugin` config shape only (`requireAsync` + `chunkName`), which is
 * what every production loadable is; `.load()` / `.preload()` go through `requireAsync` too, so
 * hover-prefetch fetches strings with the chunk. Untransformed thunks pass through unchanged (dev
 * builds, which emit no map either).
 *
 * Known gap: if the chunk's modules are ALREADY installed in webpack when the component first mounts
 * (`isReady` → `requireSync`), @loadable/component renders synchronously and skips `requireAsync`.
 * That only happens after a previous async load of the same chunk — which loaded the namespaces —
 * so in practice the store is already warm.
 */
const withNamespacePreload = (arg: unknown): unknown => {
  if (!(arg && typeof arg === 'object' && typeof (arg as BabelLoadableConfig).requireAsync === 'function')) {
    return arg;
  }
  const config = arg as BabelLoadableConfig;
  const chunkName = typeof config.chunkName === 'function' ? config.chunkName() : undefined;
  const originalRequireAsync = config.requireAsync;
  const requireAsync = function (this: unknown, ...args: unknown[]) {
    const load = () => originalRequireAsync.apply(this, args);
    const namespaces = chunkName ? globalWindow.__I18N_CHUNK_NAMESPACES__?.[chunkName] : undefined;
    if (!namespaces || namespaces.length === 0) {
      return load();
    }
    rememberPreloaded(namespaces);
    return Promise.all([load(), loadNamespacesForChunk(namespaces, chunkName)]).then(([loadedModule]) => loadedModule);
  };
  return { ...config, requireAsync };
};

/** `i18n.loadNamespaces` that degrades instead of failing the chunk. */
const loadNamespacesForChunk = (namespaces: ReadonlyArray<string>, chunkName: string | undefined): Promise<void> =>
  i18n.loadNamespaces(namespaces).catch((err: unknown) => {
    // The chunk still renders; a namespace that failed to load falls back to the component-level
    // suspend that existed before — the old behavior for that one component, not a broken route.
    addAction('ChunkNamespacePreloadFailed', {
      chunkName,
      namespaces: namespaces.join(','),
      message: String((err as { message?: string } | null | undefined)?.message ?? err),
    });
  });

/**
 * Every namespace preloaded for some chunk, so a later language switch can bring the new locale's
 * bundles for everything already on screen instead of letting each component suspend on its own.
 */
const preloadedNamespaces = new Set<string>();
let languageChangeListenerInstalled = false;
const rememberPreloaded = (namespaces: ReadonlyArray<string>): void => {
  namespaces.forEach(namespace => preloadedNamespaces.add(namespace));
  if (!languageChangeListenerInstalled && typeof i18n.on === 'function') {
    languageChangeListenerInstalled = true;
    i18n.on('languageChanged', () => {
      if (preloadedNamespaces.size > 0) {
        void loadNamespacesForChunk([...preloadedNamespaces], 'languageChanged');
      }
    });
  }
};

/**
 * Create a lazy-loaded React component.
 *
 * **For default exports** — omit `exportName`:
 * ```typescript
 * const MyComp = loadable(() => import('./MyComp'));
 * ```
 *
 * **For named exports** — pass the export name as the 2nd argument:
 * ```typescript
 * const MyComp = loadable(() => import('./MyComp'), 'MyComp');
 * ```
 *
 * SSR-safe because:
 * 1. Exported with the name `loadable`, which `@loadable/babel-plugin` recognizes
 *    through name-based pattern matching on the local identifier.
 * 2. Named-export path uses `resolveComponent` (not `{ default: ... }` wrapping)
 *    to avoid hoist-non-react-statics race conditions.
 *
 * Both code paths also load the chunk's i18n namespaces with the chunk (see `withNamespacePreload`
 * above), and both silently retry on chunk-load failure — up to 2 attempts with a 1s/4s
 * backoff, each re-fetching the chunk with a cache-busting query param, under a total
 * ~40s budget. See doc/plans/chunk-retry-backoff.md.
 *
 * **Do NOT rename the import** — the babel plugin requires the local identifier `loadable`:
 * ```typescript
 * import { loadable } from '@shared/core/loadableFromNamedExport'; // OK
 * import { loadable as loadableX } from '@shared/core/loadableFromNamedExport'; // BREAKS SSR
 * ```
 *
 * `loadable.lib()` for non-component dynamic imports is exposed as a property on this
 * same `loadable` export — it applies the same silent chunk-retry around the library
 * loader while keeping the `.lib` render-prop API. Import `loadable` from here and call
 * `loadable.lib(...)` rather than importing `@loadable/component` directly; the babel
 * plugin matches `<ident>.lib(...)` where `<ident>` is named `loadable`, so the callsite
 * keeps its SSR transform (`requireSync`/`resolve`/`chunkName`) — which
 * `@loadable/component` asserts on whenever a `ChunkExtractor` is in context, `ssr: false`
 * or not.
 *
 * @see https://loadable-components.com/docs/api-loadable-component/
 * @see https://github.com/gregberge/loadable-components/issues/420
 */
// Mirrors @loadable/component's `OptionsWithoutResolver<Props>`. `resolveComponent` is
// owned by the named-export overload (set internally from `exportName`), so it's not
// exposed here.
type LoadableOptions<Props = unknown> = Readonly<{
  fallback?: JSX.Element;
  ssr?: boolean;
  cacheKey?: (props: Props) => unknown;
}>;

// Overload A: default-export (omit `exportName`)
export function loadable<P>(importFn: () => Promise<{ default: ComponentType<P> }>, options?: LoadableOptions): LoadableComponent<P>;

// Overload B: named-export
export function loadable<TModule extends Record<string, unknown>, TExportName extends keyof TModule & string>(
  importFn: () => Promise<TModule>,
  exportName: TExportName,
  options?: LoadableOptions,
): LoadableComponent<ExtractComponentProps<TModule[TExportName]>>;

// Implementation — the public API surface is constrained by the overloads above; this body
// uses `unknown` at the input boundary and casts at the single point where we hand off to
// @loadable/component (which accepts either a thunk OR the babel-plugin-transformed config
// object as its first arg — a runtime contract not reflected in its TS types).
export function loadable(importFn: unknown, second?: string | LoadableOptions, third?: LoadableOptions): LoadableComponent<unknown> {
  const wrappedLoader = withNamespacePreload(wrapLoaderArg(importFn)) as () => Promise<unknown>;
  if (typeof second === 'string') {
    const exportName = second;
    const options = third;
    return loadableOriginal(wrappedLoader, {
      resolveComponent: (m: unknown) => (m as Record<string, ComponentType<unknown>>)[exportName],
      ...options,
    });
  }
  // The no-resolver overload of @loadable/component requires the loader to resolve to a
  // `DefaultComponent` (module with a default export). The runtime accepts the babel-plugin
  // config object too — cast to satisfy the stricter typed signature.
  return loadableOriginal(wrappedLoader as () => Promise<DefaultComponent<unknown>>, { ...second });
}

/**
 * Retry-wrapped `loadable.lib()` for non-component dynamic imports (filestack-js,
 * lottie-web, `@vimeo/player`). Same silent-retry + cache-bust behavior as `loadable()`:
 * `wrapLoaderArg` handles both the untransformed thunk and the babel-plugin config-object
 * shape, so `.lib` chunks get the same `?retry=N` recovery as component chunks.
 *
 * Attached as a property on `loadable` (rather than exported under its own name) because
 * `@loadable/babel-plugin` matches `.lib` callsites syntactically: `<ident>.lib(...)` where
 * `<ident>` is named `loadable` (see its `isValidIdentifier`). A standalone
 * `loadableLib(...)` callsite is invisible to the plugin, so its `() => import(...)` is
 * left as a plain thunk — and `@loadable/component`'s
 * `invariant(!props.__chunkExtractor || ctor.requireSync, 'SSR requires @loadable/babel-plugin')`
 * runs *before* the `ssr: false` early-return, so an untransformed `.lib` throws during SSR
 * on any page that renders it (e.g. `LottiePlayer`, which renders its lib unconditionally).
 * Keep the callsite shape as `loadable.lib(...)`;
 * test/build-contracts/loadableLibTransform.contract.spec.ts pins this.
 */
const lib = <TModule>(importFn: () => Promise<TModule>, options?: LoadableOptions): LoadableLibrary<TModule> => {
  const wrappedLoader = wrapLoaderArg(importFn) as () => Promise<TModule>;
  return loadableOriginal.lib(wrappedLoader as () => Promise<TModule>, options);
};

loadable.lib = lib;

// Internal exports for unit testing only.
/** Test hook: forget preloaded chunks and re-arm the one-time `languageChanged` subscription. */
const resetNamespacePreloadState = (): void => {
  preloadedNamespaces.clear();
  languageChangeListenerInstalled = false;
};

export const __testing__ = { isChunkLoadError, withChunkRetry, wrapLoaderArg, withQueryParam, withNamespacePreload, resetNamespacePreloadState };
